// Configuración del cliente API para Yawi
import type { ApiError } from './ApiError';
import type { StorageClient } from '../storage/StorageClient';
import { createWebStorageClient } from '../storage/StorageClient';
import { createHttpClient, HttpClient, TransientAuthError } from './HttpClient';

let apiClient: HttpClient | null = null;
let apiBaseUrl: string | null = null;
let authToken: string | null = null;
let refreshToken: string | null = null; // Solo mobile: en web viaja en cookie HttpOnly, nunca en JS.
let csrfToken: string | null = null; // Solo web: requerido por /auth/refresh y /auth/logout.
let onUnauthorizedCallback: (() => void) | null = null;
let onForbiddenCallback: ((message: string, method: string, code?: string) => void) | null = null;
let onRateLimitedCallback: ((retryAfter?: number) => void) | null = null;
let onTokenCompromisedCallback: (() => void) | null = null;
let storageClient: StorageClient | null = null;
let activeSectionId: string | null = null; // Selector global del admin de sección (x-section-id).
let isHandlingUnauthorized = false; // Guard para evitar múltiples llamadas al callback

// Coordinación entre pestañas del mismo origen (solo web): el refresh token
// es de un solo uso y viaja en una cookie COMPARTIDA por todas las pestañas,
// así que si dos pestañas piden /auth/refresh casi al mismo tiempo, la
// segunda manda un csrfToken ya rotado y el backend la rechaza como "sesión
// comprometida" (falso positivo, no es un ataque real). `AUTH_REFRESH_LOCK`
// serializa el refresh real vía la Web Locks API (nativa del browser, sin
// estado propio) para que nunca haya dos requests de refresh en vuelo a la
// vez; `authSyncChannel` avisa a las demás pestañas del resultado para que
// adopten el token nuevo en memoria en vez de competir por su propio refresh.
const AUTH_REFRESH_LOCK_NAME = 'yawi-auth-refresh';
const AUTH_SYNC_CHANNEL_NAME = 'yawi-auth-sync';
let authSyncChannel: BroadcastChannel | null = null;

// true en cualquier entorno de browser (apps/web, apps/web-admin), false en
// React Native (apps/mobile, apps/mobile-admin) — determina si el refresh
// token vive en cookie HttpOnly (web) o se maneja manualmente (mobile, vía
// SecureStore). Se detecta por entorno real, no por si se pasó un
// storageClient custom: apps/web pasa explícitamente createWebStorageClient().
let isWebPlatform = false;

interface TokensDto {
  accessToken: string;
  refreshToken?: string; // Ausente en clientes web: el backend lo omite del body.
  csrfToken?: string; // Solo presente en clientes web.
  expiresIn?: number;
}

// Detecta si estamos en un browser (vs. React Native). OJO: ni siquiera
// `typeof localStorage` es seguro por sí solo — cuando el usuario bloquea
// todo el storage del sitio, `localStorage` es un getter en `window` que
// tira SecurityError al ser leído, y `typeof` igual dispara ese getter (a
// diferencia de un identificador realmente no declarado). Si tirar es en sí
// mismo la prueba de que estamos en un browser: React Native no tiene esa
// property en absoluto, ahí `typeof localStorage` da 'undefined' sin tirar.
const isBrowserEnvironment = (): boolean => {
  try {
    return typeof localStorage !== 'undefined';
  } catch {
    return true;
  }
};

// Lee la cookie yawi_csrf (no-HttpOnly, a propósito legible) para recuperar
// el csrfToken tras un reload de página, cuando la copia en memoria se perdió.
const getCsrfTokenFromCookie = (): string | null => {
  if (typeof document === 'undefined') return null;
  try {
    const match = document.cookie.match(/(?:^|;\s*)yawi_csrf=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    // Defensivo: si el navegador bloquea el acceso a document.cookie bajo
    // alguna política extrema, tratamos como "no hay sesión" en vez de
    // crashear (mismo criterio que con localStorage bloqueado).
    return null;
  }
};

// Indica si hay una posible sesión web restaurable (cookie de refresh token
// presente). Se usa en el boot de la app para decidir si vale la pena
// intentar restaurar sesión antes de mandar al usuario a login.
export const hasWebSessionCookie = (): boolean => getCsrfTokenFromCookie() !== null;

export interface ApiConfig {
  baseUrl: string;
  onUnauthorized?: () => void;
  onForbidden?: (message: string, method: string, code?: string) => void;
  onRateLimited?: (retryAfter?: number) => void;
  onTokenCompromised?: () => void;
  storageClient?: StorageClient; // Opcional: permite inyectar un storage client personalizado
}

export const initApiClient = (config: ApiConfig): HttpClient => {
  onUnauthorizedCallback = config.onUnauthorized ?? null;
  onForbiddenCallback = config.onForbidden ?? null;
  onRateLimitedCallback = config.onRateLimited ?? null;
  onTokenCompromisedCallback = config.onTokenCompromised ?? null;
  apiBaseUrl = config.baseUrl;

  // Detectar entorno real (no si se pasó un storageClient custom): localStorage
  // solo existe en browser (apps/web, apps/web-admin), nunca en React Native.
  isWebPlatform = isBrowserEnvironment();

  if (isWebPlatform && typeof BroadcastChannel !== 'undefined') {
    authSyncChannel?.close();
    authSyncChannel = new BroadcastChannel(AUTH_SYNC_CHANNEL_NAME);
    authSyncChannel.onmessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; accessToken?: string; csrfToken?: string } | null;
      if (data?.type === 'tokens-refreshed' && data.accessToken) {
        // Otra pestaña ya renovó la sesión: adoptamos su resultado en memoria
        // en vez de competir por el mismo refresh token de un solo uso.
        authToken = data.accessToken;
        if (data.csrfToken) csrfToken = data.csrfToken;
        isHandlingUnauthorized = false;
      }
    };
  }

  // Inicializar storage client (usar el proporcionado o crear uno por defecto para web)
  if (config.storageClient) {
    storageClient = config.storageClient;
  } else if (isWebPlatform) {
    storageClient = createWebStorageClient();
  } else {
    // En mobile, se debe proporcionar un storageClient
    throw new Error('StorageClient must be provided for mobile environment. Pass it in ApiConfig.');
  }

  apiClient = createHttpClient({
    baseUrl: config.baseUrl,
    timeout: 30000,
    getAuthToken: () => authToken,
    getTenantId: () => currentTenant?.id ?? null,
    getSectionId: () => activeSectionId,
    getClientType: isWebPlatform ? () => 'web' : undefined,
    getCsrfToken: isWebPlatform ? () => csrfToken ?? getCsrfTokenFromCookie() : undefined,
    onRefreshToken: async () => {
      // Intentar refresh token - retorna true si exitoso. En web siempre se
      // intenta (el browser decide si hay cookie que mandar); en mobile solo
      // tiene sentido si hay un refreshToken cargado en memoria.
      if (!isWebPlatform && !refreshToken) return false;
      try {
        await refreshSession();
        return true;
      } catch (err) {
        // Red caída, timeout o el backend de /auth/refresh respondiendo 5xx:
        // no es que el refresh token sea inválido, así que se relanza para
        // que HttpClient NO cierre la sesión - solo falla este intento.
        if (err instanceof TransientAuthError) throw err;
        return false;
      }
    },
    onUnauthorized: async () => {
      // Se llama cuando el refresh falló o no hay refresh token
      // Guard para evitar múltiples llamadas simultáneas
      if (isHandlingUnauthorized) return;
      isHandlingUnauthorized = true;

      await clearTokens();
      onUnauthorizedCallback?.();
      // No reseteamos el flag aquí - el usuario debe hacer login de nuevo
    },
    onForbidden: (error: ApiError, method: string) => {
      onForbiddenCallback?.(error.message, method, error.code);
    },
    onRateLimited: (retryAfter?: number) => {
      onRateLimitedCallback?.(retryAfter);
    },
  });

  // Cargar tokens del storage si existen (no bloqueante)
  loadTokensFromStorage().catch(() => {
    // Ignore errors during token loading
  });

  // Idem con el tenant: sin esto, tras un reload `currentTenant` queda en null
  // hasta el próximo login, así que ni se manda x-tenant-id ni la UI puede
  // mostrar en qué residencial está la sesión.
  loadTenantFromStorage().catch(() => {
    // Ignore errors during tenant loading
  });

  return apiClient;
};

export const loadTokensFromStorage = async (): Promise<void> => {
  if (!storageClient) return;
  
  try {
    const storedAuth = await storageClient.getItem('yawi_auth');
    if (storedAuth) {
      // El accessToken nunca se persiste (solo vive en memoria): si no hay uno
      // cargado todavía, la primera request devuelve 401 y el flujo de refresh
      // automático de HttpClient lo repone usando el refreshToken. Esto reduce
      // la ventana en la que un XSS podría leer un access token válido directo
      // de localStorage.
      const { refreshToken: rt } = JSON.parse(storedAuth);
      refreshToken = rt;
    }
  } catch {
    // Ignore parse errors
  }
};

export const getApiClient = (): HttpClient => {
  if (!apiClient) {
    throw new Error('API client not initialized. Call initApiClient first.');
  }
  return apiClient;
};

export const setTokens = async (accessToken: string, refresh?: string, csrf?: string): Promise<void> => {
  authToken = accessToken;
  isHandlingUnauthorized = false; // Reset guard on new tokens

  // Defensa en profundidad: en web nunca guardamos un refreshToken, ni en
  // memoria ni en storage, aunque llegue en el body de la respuesta. Esto
  // cubre endpoints que un backend pueda no haber migrado todavía al esquema
  // de cookie (ej. /auth/select-tenant sigue devolviéndolo en texto plano
  // incluso con x-client-type: web) — no queremos depender de que TODOS los
  // endpoints de auth estén actualizados para no persistir el secreto.
  if (refresh && !isWebPlatform) {
    refreshToken = refresh;
    if (storageClient) {
      await storageClient.setItem('yawi_auth', JSON.stringify({ refreshToken }));
    }
  }

  // Solo web recibe csrfToken; vive en memoria y se puede recuperar de la
  // cookie yawi_csrf tras un reload (ver getCsrfTokenFromCookie).
  if (csrf) {
    csrfToken = csrf;
  }
};

export const clearTokens = async (): Promise<void> => {
  authToken = null;
  refreshToken = null;
  csrfToken = null;
  if (storageClient) {
    await storageClient.removeItem('yawi_auth');
  }
};

export const getAccessToken = () => authToken;
export const getRefreshToken = () => refreshToken;

// Tenant storage
// El backend manda el tenant completo en el login (TenantResponseDto: branding,
// features y config incluidos) y eso es lo que se persiste — el tipo solo
// declaraba id/name/slug, así que el consumidor no podía leer el resto sin
// castear.
interface StoredTenant {
  id: string;
  name: string;
  slug: string;
  branding?: Record<string, unknown>;
  features?: Record<string, boolean>;
  config?: Record<string, string>;
}

let currentTenant: StoredTenant | null = null;

export const setCurrentTenant = async (tenant: StoredTenant | null): Promise<void> => {
  currentTenant = tenant;
  if (storageClient) {
    if (tenant) {
      await storageClient.setItem('yawi_tenant', JSON.stringify(tenant));
    } else {
      await storageClient.removeItem('yawi_tenant');
    }
  }
};

export const getCurrentTenant = (): StoredTenant | null => currentTenant;

export const clearCurrentTenant = async (): Promise<void> => {
  currentTenant = null;
  if (storageClient) {
    await storageClient.removeItem('yawi_tenant');
  }
};

export const loadTenantFromStorage = async (): Promise<StoredTenant | null> => {
  if (storageClient) {
    const stored = await storageClient.getItem('yawi_tenant');
    if (stored) {
      currentTenant = JSON.parse(stored);
      return currentTenant;
    }
  }
  return null;
};

// Resetear el guard de unauthorized (llamar después de login exitoso)
export const resetUnauthorizedGuard = (): void => {
  isHandlingUnauthorized = false;
};

// Permite actualizar el callback de onUnauthorized después de inicializar
export const setOnUnauthorizedCallback = (callback: () => void): void => {
  onUnauthorizedCallback = callback;
};

// Permite actualizar el callback de onRateLimited después de inicializar
export const setOnRateLimitedCallback = (callback: (retryAfter?: number) => void): void => {
  onRateLimitedCallback = callback;
};

// Permite actualizar el callback de onForbidden después de inicializar
export const setOnForbiddenCallback = (callback: (message: string, method: string, code?: string) => void): void => {
  onForbiddenCallback = callback;
};

// Retorna la URL base del API (sin versión)
export const getApiBaseUrl = (): string => apiBaseUrl ?? '';

// Permite actualizar el callback de onTokenCompromised después de inicializar
export const setOnTokenCompromisedCallback = (callback: () => void): void => {
  onTokenCompromisedCallback = callback;
};

// Sección activa del admin de sección: el backend acota lecturas a ella.
export const setActiveSectionId = (sectionId: string | null): void => {
  activeSectionId = sectionId;
};

export const tenantHeaders = (tenantId?: string): { headers?: Record<string, string> } =>
  tenantId ? { headers: { 'x-tenant-id': tenantId } } : {};

const REFRESH_TIMEOUT_MS = 15000; // Menor que executeRequest (30s): un refresh colgado no debe bloquear el retry tanto tiempo.

// Ejecuta la request de refresh con un csrfToken puntual. Separado de
// refreshSession para poder reintentar con un valor distinto sin duplicar
// la lógica de armado de headers/body.
const requestRefresh = async (csrf: string | null | undefined): Promise<Response> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
  let body: string | undefined;

  if (isWebPlatform) {
    // El refresh token viaja en la cookie HttpOnly yawi_rt (el browser la
    // adjunta solo); acá solo mandamos el CSRF requerido por el backend.
    if (!csrf) {
      throw new Error('No hay sesión web activa');
    }
    headers['x-client-type'] = 'web';
    headers['x-csrf-token'] = csrf;
  } else {
    body = JSON.stringify({ refreshToken });
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
  try {
    return await fetch(`${apiBaseUrl}/v1/auth/refresh`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body,
      signal: controller.signal,
    });
  } catch {
    // fetch rechaza por red caída, DNS, timeout (abort) - no por credenciales
    // inválidas. Tratar como transitorio: no cerrar sesión por esto.
    throw new TransientAuthError('Network error during token refresh');
  } finally {
    clearTimeout(timeoutId);
  }
};

// Serializa el refresh real entre pestañas del mismo origen vía la Web Locks
// API nativa del browser: mientras una pestaña sostiene el lock, cualquier
// otra que lo pida queda en cola en vez de disparar su propio /auth/refresh
// en paralelo contra la misma cookie de un solo uso. En browsers sin
// soporte (o en mobile, donde no existe `navigator`), se ejecuta sin lock —
// mismo comportamiento que antes de este cambio.
const withRefreshLock = async <T>(fn: () => Promise<T>): Promise<T> => {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return fn();
  return locks.request(AUTH_REFRESH_LOCK_NAME, fn);
};

// Avisa a las demás pestañas que ya hay un access token nuevo, para que lo
// adopten en memoria en vez de competir por su propio refresh.
const broadcastTokensRefreshed = (accessToken: string, csrf?: string): void => {
  authSyncChannel?.postMessage({ type: 'tokens-refreshed', accessToken, csrfToken: csrf });
};

// Permite pedir un access token nuevo de forma proactiva (ej. al restaurar
// sesión en el boot de la app) en vez de esperar a que una request de
// negocio falle con 401 primero. Mismo efecto que el flujo reactivo de
// HttpClient (también la usa internamente vía onRefreshToken), pero sin el
// 401 "esperado" de por medio ni la request de negocio duplicada.
export const refreshSession = async (): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error('API base URL not configured');
  }
  if (!isWebPlatform && !refreshToken) {
    throw new Error('No refresh token available');
  }

  await withRefreshLock(async () => {
    // Snapshot the token this refresh is for. If something else (e.g. exiting
    // impersonation) replaces the in-memory refreshToken while this request is
    // in flight, the response below is stale and must not overwrite the newer one.
    const refreshTokenAtStart = refreshToken;

    //si apiBaseUrl termina con /, quitarla para evitar // en la URL
    if (apiBaseUrl!.endsWith('/')) {
      apiBaseUrl = apiBaseUrl!.slice(0, -1);
    }
    // La cookie yawi_csrf es la fuente de verdad compartida entre pestañas:
    // se prefiere siempre por sobre la copia en memoria, que puede haber
    // quedado vieja si otra pestaña roto la sesión mientras esperábamos el
    // lock (o antes de pedirlo). Solo se cae a la copia en memoria si por
    // alguna razón la cookie no se puede leer.
    let usedCsrf = isWebPlatform ? (getCsrfTokenFromCookie() ?? csrfToken) : undefined;
    let response = await requestRefresh(usedCsrf);
    let retriedWithFreshCookie = false;

    while (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = typeof errorData.message === 'string' ? errorData.message : '';
      const isCompromisedSignal = response.status === 401 && (message.includes('comprometida') || message.includes('compromised'));

      // Red de seguridad adicional (el lock ya debería evitar esto en
      // browsers compatibles): si el backend igual reporta el token como
      // comprometido pero la cookie yawi_csrf cambió desde que la leímos,
      // es otra pestaña ganándonos la carrera, no un atacante — reintentamos
      // una única vez con el valor más reciente.
      if (isCompromisedSignal && isWebPlatform && !retriedWithFreshCookie) {
        const latestCsrf = getCsrfTokenFromCookie();
        if (latestCsrf && latestCsrf !== usedCsrf) {
          retriedWithFreshCookie = true;
          usedCsrf = latestCsrf;
          response = await requestRefresh(usedCsrf);
          continue;
        }
      }

      if (isCompromisedSignal) {
        await clearTokens();
        onTokenCompromisedCallback?.();
        throw new Error('Token compromised');
      }

      // El backend está caído/degradado o rate-limitando la request, no que el
      // refresh token sea inválido (eso es un 401 sin señal de compromiso,
      // manejado por el throw de abajo). Con varias pestañas abiertas, el
      // tráfico de fondo (polling, requests en paralelo) puede gatillar el
      // rate limit justo en este endpoint — no hay que cerrar sesión por eso.
      if (response.status >= 500 || response.status === 429) {
        throw new TransientAuthError(`Refresh backend error: ${response.status}`);
      }

      throw new Error('Refresh failed');
    }

    const json = await response.json();
    const tokens = (json.data ?? json.tokens ?? json) as Partial<TokensDto>;

    if (!tokens?.accessToken) {
      throw new Error('Invalid refresh response');
    }

    // Someone else already replaced the session (e.g. impersonation exit) —
    // discard this stale result instead of clobbering the newer tokens.
    if (refreshToken !== refreshTokenAtStart) {
      return;
    }

    await setTokens(tokens.accessToken, tokens.refreshToken, tokens.csrfToken);
    if (isWebPlatform) {
      broadcastTokensRefreshed(tokens.accessToken, tokens.csrfToken ?? csrfToken ?? undefined);
    }
  });
};
